import { Group, Stack } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { keepPreviousData } from "@tanstack/react-query"
import { RowSelectionState } from "@tanstack/react-table"
import { Loader, AreYouSureModal, FilterBuilder, ColumnManager } from "@tfg/ui"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { TABLE_CONTEXT_MAP } from "@tfg/types/schemas/modules/view/constants/table-context-map"

import { SortBuilder } from "~/components/SortBuilder"
import { CustomTable } from "~/components/table/CustomTable"
import { SearchBar } from "~/components/table/SearchBar"
import { ViewHeader } from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import {
  FindCatalogProductsQueryResponse,
  FindProductsCatalogRequestFiltersDto,
  useFindCatalogProducts,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { CatalogActionsMenu } from "~/pages/Brand/Products/List/Components/ProductsTables/catalog/CatalogActionsMenu"
import { useCatalogTable } from "~/pages/Brand/Products/List/Components/ProductsTables/catalog/useCatalogTable"
import {
  useDeleteProductsMutation,
  useDownloadProductsCSVMutation,
} from "~/pages/Brand/Products/List/hooks"
import { useBuildProductFilters } from "~/pages/Brand/Products/List/hooks/useBuildProductFilters"
import { useBuildProductSorts } from "~/pages/Brand/Products/List/hooks/useBuildProductSorts"

export const CatalogProductsTable = () => {
  const { t } = useTranslation(["products", "common"])

  const [
    isDeleteModalOpen,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false)

  const [params] = useSearchParams()

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const columnManagerTranslations = useGetColumnManagerTranslations()

  const incomingLaunchDate = params.get("incomingLaunchDate")

  const defaultFilters = useMemo(() => {
    if (!incomingLaunchDate) {
      return {} as FindProductsCatalogRequestFiltersDto
    }
    return {
      incomingLaunchDate,
    }
  }, [incomingLaunchDate])

  const { title, searchPlaceholder, options, filtersValue, setFiltersValue } =
    useBuildProductFilters<FindProductsCatalogRequestFiltersDto>(
      [
        {
          label: t("products:lists.placeholders.launchDate"),
          key: "incomingLaunchDate",
          options: [
            {
              value: "incoming",
              label: t("products:lists.placeholders.incoming"),
            },
            {
              value: "expired",
              label: t("products:lists.placeholders.expired"),
            },
            { value: "all", label: t("products:lists.placeholders.allF") },
          ],
          type: "select",
        },
      ],
      defaultFilters
    )

  const { sorts, setSorts, sortOptions } = useBuildProductSorts()

  const {
    data: productsData,
    isPending: isLoadingProducts,
    setPageBasedPagination,
    pageBasedPagination,
    cursorBasedPagination,
    totalPages,
  } = usePaginatedQuery<
    typeof useFindCatalogProducts,
    FindCatalogProductsQueryResponse
  >(
    useFindCatalogProducts,
    {
      filters: filtersValue,
      sorts,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    }
  )

  const products = productsData?.data ?? []

  const { mutate: exportAll, isPending: isProductExportLoading } =
    useDownloadProductsCSVMutation({
      filters: filtersValue,
    })

  const { mutate: deleteProducts, isPending: isProductsDeleteLoading } =
    useDeleteProductsMutation({
      filters: filtersValue,
      sorts,
      fetchDataOptions: cursorBasedPagination,
      onDeleteSettled: () => {
        setRowSelection({})
        closeDeleteModal()
      },
    })

  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false)

  const table = useCatalogTable({
    pagination: pageBasedPagination,
    rowSelection,
    setPagination: setPageBasedPagination,
    setRowSelection,
    pageCount: totalPages,
    products,
  })

  const selectedProductIds = useMemo(() => {
    return Object.keys(rowSelection).filter((row) => rowSelection[row])
  }, [rowSelection])

  return (
    <>
      <Stack>
        <Views tableContext={TABLE_CONTEXT_MAP.PRODUCTS_CATALOG} />
        <Group>
          <SearchBar
            filtersValue={filtersValue}
            setFiltersValue={setFiltersValue}
            placeholder={t("products:lists.placeholders.searchProduct")}
            isFetching={isLoadingProducts}
          />
          <SortBuilder
            sorts={sorts}
            setSorts={setSorts}
            options={sortOptions}
          />
          <FilterBuilder
            title={title}
            searchPlaceholder={searchPlaceholder}
            options={options}
            setFilters={setFiltersValue}
            filters={filtersValue}
          />
          <ColumnManager
            table={table}
            translations={columnManagerTranslations}
            isOpen={isColumnManagerOpen}
            setIsOpen={setIsColumnManagerOpen}
          />
          <ViewHeader tableContext={TABLE_CONTEXT_MAP.PRODUCTS_CATALOG} />
          <Group style={{ marginLeft: "auto" }}>
            <CatalogActionsMenu
              selectedProductIds={selectedProductIds}
              onProductExportClick={exportAll}
              isProductExportLoading={isProductExportLoading}
              onProductsDeleteClick={openDeleteModal}
              isProductsDeleteLoading={isProductsDeleteLoading}
            />
          </Group>
        </Group>
        {isLoadingProducts ? (
          <Loader fullPage />
        ) : (
          <CustomTable
            table={table}
            manualPaginationCount={productsData?.count || 0}
            stickyHeader
            layout="fixed"
            setOpenColumnManager={setIsColumnManagerOpen}
          />
        )}
      </Stack>
      <AreYouSureModal
        opened={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={() => {
          if (selectedProductIds.length === 0) {
            return
          }
          deleteProducts({ data: { productIds: selectedProductIds } })
        }}
        areYouSureText={t("products:bulkActions.delete.modal.message")}
        confirmText={t("products:bulkActions.delete.modal.confirm")}
        cancelText={t("products:bulkActions.delete.modal.cancel")}
        title={t("products:bulkActions.delete.modal.title")}
      />
    </>
  )
}
